import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { AsyncPipe, NgClass, NgOptimizedImage } from '@angular/common';
import { ISbImageLoadParams } from '@seven1/model';
import { BreakpointsService } from '@seven1/angular/media';
import { ImageService } from '@seven1/angular-storyblok/image';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { IDecoratedSectionBlock } from './decorated-section.model';
import { CmsComponent } from '@seven1/angular-storyblok-cms';

@Component({
    selector: 'section[app-decorated-section]',
    imports: [CmsComponent, NgOptimizedImage, NgClass, AsyncPipe],
    templateUrl: './decorated-section.component.html',
    styleUrl: './decorated-section.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'app-decorated-section',
        '[attr.id]': 'block()?.componentId'
    }
})
export class DecoratedSectionComponent {
    private readonly _images = inject(ImageService);
    private readonly _tailwind = inject(TailwindService);
    breakpoints = inject(BreakpointsService);
    block = input.required<IDecoratedSectionBlock>();
    sizes = input<string>('100vw');

    classes = computed(() => {
        const blok = this.block();
        const bottomChildren = blok?.childrenBottom ? blok?.childrenBottom?.length : 0;
        return [
            bottomChildren > 0 ? 'pb-32 lg:pb-80' : 'pb-10 md:pb-20',
            !blok.paddingTop ? 'pt-32' : "",
            !blok.paddingTopTablet ? 'md:pt-38' : "",
            !blok.paddingTopDesktop ? 'lg:pt-38' : "",
            ...this._tailwind.getBackgroundColorClasses(blok),
            ...this._tailwind.getSpacingClasses(blok),
            ...this._tailwind.getDisplayClasses(blok,"flex"),
            'items-center',
            'flex'
        ];
    });

    imageClasses = computed(() => {
        const blok = this.block();
        return [...this._tailwind.getObjectPositionClasses(blok)];
    });

    loadParams = computed<ISbImageLoadParams | undefined>(() => {
        const block = this.block();
        let params: ISbImageLoadParams | undefined = undefined;
        if (block.backgroundImage) {
            params = (this._images.extractUrlInfo(block.backgroundImage?.filename) || {}) as ISbImageLoadParams;
        }
        return params;
    });

    loadParamsTablet = computed<ISbImageLoadParams | undefined>(() => {
        const block = this.block();
        let params: ISbImageLoadParams | undefined = undefined;
        if (block.backgroundImageTablet) {
            params = (this._images.extractUrlInfo(block.backgroundImageTablet?.filename) || {}) as ISbImageLoadParams;
        }
        return params;
    });

    loadParamsDesktop = computed<ISbImageLoadParams | undefined>(() => {
        const block = this.block();
        let params: ISbImageLoadParams | undefined = undefined;
        if (block.backgroundImageDesktop) {
            params = (this._images.extractUrlInfo(block.backgroundImageDesktop?.filename) || {}) as ISbImageLoadParams;
        }
        return params;
    });
}
